import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import {
  KOKORO_LANGUAGES,
  downloadAudioBlob,
  defaultVoiceFor,
  isLanguageSupported,
  synthesizeSpeech,
  voicesForLanguage,
} from '@/lib/kokoroTts';
import { uploadToSeaweedFS } from '@/lib/seaweedfs';
import { createMedia, listMedia, type ContentMedia } from '@/lib/contentStudio';

interface GeneratedClip {
  id: string;
  name: string;
  url: string;
  blob: Blob;
  voice: string;
  chars: number;
  saved: boolean;
  savedUrl?: string;
}

const EXAMPLE_TEXTS = [
  { icon: 'ri-megaphone-line', label: 'Annonce boutique', text: 'Bonjour et bienvenue chez nous. Profitez de nos offres du jour, livraison rapide partout au Maroc.' },
  { icon: 'ri-mic-line', label: 'Présentation produit', text: 'Découvrez notre nouveau produit, conçu pour durer et faciliter votre quotidien. Disponible dès maintenant en magasin.' },
  { icon: 'ri-robot-2-line', label: 'Message d’accueil', text: 'Merci de votre appel. Notre équipe vous répond du lundi au samedi, de neuf heures à dix-huit heures.' },
];

export default function ContentStudioVoicePage() {
  const { user } = useAuth();
  const [text, setText] = useState('');
  const [lang, setLang] = useState('fr');
  const [voice, setVoice] = useState('ff_siwis');
  const [speed, setSpeed] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clips, setClips] = useState<GeneratedClip[]>([]);
  const [library, setLibrary] = useState<ContentMedia[]>([]);
  const [savingId, setSavingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);

  const voices = useMemo(() => voicesForLanguage(lang), [lang]);
  const supported = isLanguageSupported(lang);

  const loadLibrary = useCallback(async () => {
    if (!user?.id) return;
    try {
      const all = await listMedia(user.id);
      setLibrary(all.filter((m) => m.kind === 'audio').slice(0, 8));
    } catch {
      /* bibliothèque indisponible : on garde la liste locale */
    }
  }, [user?.id]);

  useEffect(() => { void loadLibrary(); }, [loadLibrary]);

  const onLanguageChange = (code: string) => {
    setLang(code);
    setVoice(defaultVoiceFor(code));
  };

  const generate = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { url, blob } = await synthesizeSpeech({ text, voice, speed });
      const clip: GeneratedClip = {
        id: `${Date.now()}`,
        name: `Voix off · ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`,
        url,
        blob,
        voice,
        chars: text.trim().length,
        saved: false,
      };
      setClips((prev) => [clip, ...prev].slice(0, 12));
      setPlayingId(clip.id);
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.src = url;
          void audioRef.current.play().catch(() => {});
        }
      }, 60);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'La génération audio a échoué.');
    } finally {
      setBusy(false);
    }
  };

  const playClip = (clip: GeneratedClip) => {
    if (!audioRef.current) audioRef.current = new Audio();
    if (playingId === clip.id) {
      audioRef.current.pause();
      setPlayingId(null);
      return;
    }
    audioRef.current.src = clip.url;
    audioRef.current.onended = () => setPlayingId(null);
    void audioRef.current.play().then(() => setPlayingId(clip.id)).catch(() => setError('Lecture impossible.'));
  };

  const saveClip = async (clip: GeneratedClip) => {
    if (!user?.id || clip.saved) return;
    setSavingId(clip.id);
    setError(null);
    try {
      const bytes = new Uint8Array(await clip.blob.arrayBuffer());
      const filename = `voix-off-${clip.id}.mp3`;
      const publicUrl = await uploadToSeaweedFS(bytes, filename, 'audio/mpeg', `users/${user.id}/content-studio/audio`);
      await createMedia({
        ownerId: user.id,
        kind: 'audio',
        url: publicUrl,
        name: `${clip.name} (${clip.voice})`,
      });
      setClips((prev) => prev.map((c) => (c.id === clip.id ? { ...c, saved: true, savedUrl: publicUrl } : c)));
      void loadLibrary();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Impossible d'enregistrer dans la médiathèque.");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <audio ref={audioRef} className="hidden" />

      <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
            <i className="ri-volume-up-line text-accent-500"></i>
            Studio Text-to-Voice
          </h2>
          <p className="text-sm text-foreground-500 mt-1">
            Convertissez n&apos;importe quel texte en voix off MP3, prête à télécharger ou à réutiliser dans vos vidéos.
          </p>
        </div>
        <Link
          to="/dashboard/content-studio"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-background-200/70 bg-background-50 text-sm font-medium text-foreground-700 whitespace-nowrap hover:border-accent-300 transition-colors cursor-pointer no-underline self-start"
        >
          <i className="ri-arrow-left-line"></i>
          Retour au studio
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Éditeur */}
        <div className="lg:col-span-2 bg-background-50 border border-background-200/70 rounded-xl p-4 md:p-5">
          <label className="block text-sm font-semibold text-foreground-800 mb-2">
            Votre texte
          </label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 3000))}
            rows={7}
            placeholder="Écrivez ou collez le texte à lire à voix haute…"
            className="w-full px-4 py-3 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-300 resize-none"
          />
          <div className="flex items-center justify-between mt-1.5 mb-3">
            <p className="text-[11px] text-foreground-400">{text.trim().length} / 3000 caractères</p>
            {text && (
              <button
                type="button"
                onClick={() => setText('')}
                className="text-[11px] text-foreground-500 hover:text-red-500 transition-colors cursor-pointer whitespace-nowrap"
              >
                Effacer
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            {EXAMPLE_TEXTS.map((ex) => (
              <button
                key={ex.label}
                type="button"
                onClick={() => setText(ex.text)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-background-200/70 text-xs text-foreground-600 hover:bg-background-100 hover:border-accent-300 transition-colors cursor-pointer"
              >
                <i className={`${ex.icon} text-accent-500`}></i>
                {ex.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Langue</label>
              <div className="relative">
                <select
                  value={lang}
                  onChange={(e) => onLanguageChange(e.target.value)}
                  className="w-full appearance-none px-3 py-2.5 pr-8 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-accent-300 cursor-pointer"
                >
                  {KOKORO_LANGUAGES.map((l) => (
                    <option key={l.code} value={l.code}>{l.flag} {l.label}</option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Voix</label>
              <div className="relative">
                <select
                  value={voice}
                  onChange={(e) => setVoice(e.target.value)}
                  className="w-full appearance-none px-3 py-2.5 pr-8 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-accent-300 cursor-pointer"
                >
                  {voices.map((v) => (
                    <option key={v.id} value={v.id}>{v.label} · {v.gender === 'F' ? 'Féminine' : 'Masculine'}</option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Vitesse : {speed.toFixed(2)}×</label>
              <input
                type="range"
                min={0.6}
                max={1.6}
                step={0.05}
                value={speed}
                onChange={(e) => setSpeed(Number(e.target.value))}
                className="w-full mt-3.5 accent-[oklch(var(--accent-500))] cursor-pointer"
              />
            </div>
          </div>

          {!supported && (
            <div className="mt-3 flex items-start gap-2 text-xs text-secondary-900 bg-secondary-100 px-3 py-2 rounded-lg">
              <i className="ri-information-line mt-0.5"></i>
              L&apos;arabe et la darija ne sont pas encore couverts par le moteur vocal : la voix française est utilisée.
            </div>
          )}

          <div className="flex items-center justify-between gap-3 mt-5">
            <p className="text-[11px] text-foreground-400 flex items-center gap-1.5">
              <i className="ri-shield-check-line text-accent-500"></i>
              Serveur vocal Zifek · aucun coût par caractère
            </p>
            <button
              type="button"
              onClick={generate}
              disabled={!text.trim() || busy}
              className="flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-bold whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              {busy ? <><i className="ri-loader-4-line animate-spin"></i>Génération…</> : <><i className="ri-play-circle-line"></i>GÉNÉRER LA VOIX</>}
            </button>
          </div>

          {error && (
            <div className="mt-3 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
              <i className="ri-error-warning-line"></i>{error}
            </div>
          )}
        </div>

        {/* Mes générations */}
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
          <h3 className="text-sm font-bold text-foreground-900 flex items-center gap-2 mb-1">
            <i className="ri-file-music-line text-foreground-500"></i>
            Mes voix générées
          </h3>
          <p className="text-[11px] text-foreground-500 mb-3">Téléchargez ou enregistrez dans la médiathèque.</p>

          {clips.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mb-2">
                <i className="ri-mic-line text-lg text-foreground-400"></i>
              </div>
              <p className="text-xs text-foreground-500 max-w-[180px]">Vos fichiers audio apparaîtront ici après génération.</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[420px] overflow-y-auto">
              {clips.map((c) => (
                <div key={c.id} className="rounded-lg border border-background-200/70 p-3">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => playClip(c)}
                      className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-colors cursor-pointer ${
                        playingId === c.id ? 'bg-accent-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                      }`}
                      title={playingId === c.id ? 'Pause' : 'Écouter'}
                    >
                      <i className={playingId === c.id ? 'ri-pause-line' : 'ri-play-line'}></i>
                    </button>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-foreground-800 truncate">{c.name}</p>
                      <p className="text-[10px] text-foreground-400 truncate">{c.voice} · {c.chars} car.</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 mt-2">
                    <button
                      type="button"
                      onClick={() => downloadAudioBlob(c.blob, `voix-off-${c.id}.mp3`)}
                      className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-md bg-background-100 text-[11px] font-medium text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-download-2-line"></i>Télécharger
                    </button>
                    <button
                      type="button"
                      onClick={() => saveClip(c)}
                      disabled={c.saved || savingId === c.id}
                      className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-md bg-accent-100 text-[11px] font-medium text-accent-800 hover:bg-accent-200/70 disabled:opacity-60 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {savingId === c.id ? (
                        <><i className="ri-loader-4-line animate-spin"></i>…</>
                      ) : c.saved ? (
                        <><i className="ri-check-line"></i>Enregistré</>
                      ) : (
                        <><i className="ri-save-3-line"></i>Médiathèque</>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {library.length > 0 && (
            <div className="mt-5 pt-4 border-t border-background-200/70">
              <h4 className="text-[11px] font-bold uppercase tracking-wider text-foreground-400 mb-2">
                Derniers audios enregistrés
              </h4>
              <div className="space-y-1.5">
                {library.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => {
                      if (!audioRef.current) audioRef.current = new Audio();
                      audioRef.current.src = m.url;
                      void audioRef.current.play().catch(() => setError('Lecture impossible.'));
                    }}
                    className="w-full flex items-center gap-2 px-2.5 py-2 rounded-md bg-background-100/70 hover:bg-background-200/60 transition-colors cursor-pointer text-left"
                  >
                    <i className="ri-play-circle-line text-accent-600"></i>
                    <span className="text-[11px] text-foreground-700 truncate flex-1">{m.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}